import '../styles/globals.scss';

import { v2_AppProvider as AppProvider } from '@crocodille/common';
import { isPageWithLayout } from '@framework/page';
import SeznamRetargeting from '@framework/seznam-retargeting';
import OaiqPixel from '@framework/oaiq-pixel';
import Smartlook from '@framework/smartlook';
import TikTokPixel from '@framework/tiktok-pixel';
import { getApp } from '@utils/app';
import { appWithTranslation } from 'next-i18next';
import { AppProps as NextAppProps } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';
import nextI18NextConfig from '../../next-i18next.config.mjs';
import GoogleAnalytics from '../components/google-analytics/google-analytics';
import { postMessageToWebView } from '../modules/webview';
import { PageMetaContext } from '@framework/page-meta';
import BaseHead from '@framework/base-head';
import { assertSome, iife } from '@crocodille/util';

const ABSOLUTE_URL = iife(() => {
  if (typeof window === 'undefined') {
    return process.env.NEXTAUTH_URL ?? 'https://bbdomu.cz';
  } else if (process.env.NEXTAUTH_URL) {
    return process.env.NEXTAUTH_URL;
  } else {
    return window.location.origin;
  }
});

function getNoLayout(page: React.ReactElement) {
  return page;
}

const App = ({ Component, router, pageProps }: NextAppProps) => {
  const { locale, events, route: currentRoute } = useRouter();

  const currentRouterRef = useRef(currentRoute);
  currentRouterRef.current = currentRoute;

  const app = useMemo(
    () =>
      getApp({
        locale: assertSome(locale ?? router.locale ?? 'cs'),
      }),
    [locale, router.locale],
  );

  const getLayout = isPageWithLayout(Component)
    ? Component.getLayout
    : getNoLayout;

  useEffect(() => {
    postMessageToWebView({
      action: 'routeChangeComplete',
      route: currentRouterRef.current,
    });

    const handleRouteChangeComplete = (route: string) => {
      postMessageToWebView({
        action: 'routeChangeComplete',
        route,
      });
    };

    events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [events]);

  return (
    <>
      <BaseHead />
      <PageMetaContext.Provider value={{ absoluteUrl: ABSOLUTE_URL }}>
        <AppProvider app={app}>
          {process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_CONTAINER_ID ? (
            <GoogleAnalytics
              containerId={
                process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_CONTAINER_ID
              }
            />
          ) : null}
          {process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID ? (
            <TikTokPixel pixelId={process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID} />
          ) : null}
          {process.env.NEXT_PUBLIC_OAIQ_PIXEL_ID ? (
            <OaiqPixel pixelId={process.env.NEXT_PUBLIC_OAIQ_PIXEL_ID} />
          ) : null}
          {process.env.SMARTLOOK_KEY && process.env.SMARTLOOK_REGION ? (
            <Smartlook
              smartlookKey={process.env.SMARTLOOK_KEY}
              smartlookRegion={process.env.SMARTLOOK_REGION}
            />
          ) : null}
          {process.env.NEXT_PUBLIC_SEZNAM_RETARGETING_ID ? (
            <SeznamRetargeting
              retargetingId={process.env.NEXT_PUBLIC_SEZNAM_RETARGETING_ID}
            />
          ) : null}
          {getLayout(<Component {...pageProps} />, pageProps)}
        </AppProvider>
      </PageMetaContext.Provider>
    </>
  );
};

export default appWithTranslation(App, nextI18NextConfig);
